/**
 * Panou statistici populație afectată.
 * 
 * Afișează în timp real populația expusă conform
 * filtrelor active (crossfilter).
 */
import { onCrossfilterChange, onCrossfilterStart, hasActiveFilters, clearAllSelections } from './crossfilter.js';
import { LABELS, UNITS } from './colormaps.js';

let panelEl = null;
let initialized = false;

/**
 * Inițializează panoul de statistici.
 * @param {string} containerId - ID-ul elementului DOM
 */
export function initPopulationPanel(containerId) {
  panelEl = document.getElementById(containerId);
  if (!panelEl) return;

  // Render starea inițială (fără filtre)
  renderEmpty();

  // Ascultă evenimentele de crossfilter
  onCrossfilterStart(onStart);
  onCrossfilterChange(onUpdate);
  initialized = true;
}

function onStart(detail) {
  if (!panelEl || !detail || !detail.hasFilter) return;
  panelEl.innerHTML = `
    <div class="pop-panel-loading">
      <div class="filter-spinner"></div>
      <span>Se procesează filtrele...</span>
    </div>
  `;
}

function renderEmpty() {
  if (!panelEl) return;
  panelEl.innerHTML = `
    <div class="pop-panel-empty">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M3 4h18M3 12h18M3 20h18" stroke-linecap="round"/>
      </svg>
      <span>Selectează un interval pe histograme<br>sau o zonă pe hartă</span>
    </div>
  `;
}

function onUpdate(result) {
  if (!panelEl) return;

  if (!result.hasFilter) {
    renderEmpty();
    return;
  }

  const s = result.stats;
  const popTotal = Number(s.total_pop || 0);
  const popLt15 = Number(s.pop_lt15 || 0);
  const pop1564 = Number(s.pop_15_64 || 0);
  const popGe65 = Number(s.pop_ge65 || 0);
  const popFemale = Number(s.pop_female || 0);
  const popMale = Number(s.pop_male || 0);
  const popEmployed = Number(s.pop_employed || 0);
  const cellCount = Number(s.cell_count || 0);
  const areaSqKm = cellCount * 100; // fiecare celulă CAMS ≈ 10×10km = 100km²

  // Medii variabile
  const avgs = result.averages || {};

  panelEl.innerHTML = `
    <div class="pop-panel-header">
      <span class="pop-panel-title">Populație afectată</span>
      <button class="pop-panel-clear" title="Șterge filtrele">✕</button>
    </div>

    <div class="pop-panel-hero">
      <div class="pop-hero-number">${formatPop(popTotal)}</div>
      <div class="pop-hero-label">persoane</div>
    </div>

    <div class="pop-panel-breakdown">
      <div class="pop-age-group">
        <div class="pop-age-icon">👶</div>
        <div class="pop-age-info">
          <div class="pop-age-value">${formatPop(popLt15)}</div>
          <div class="pop-age-label">&lt; 15 ani</div>
        </div>
        <div class="pop-age-pct">${popTotal > 0 ? ((popLt15 / popTotal) * 100).toFixed(0) : 0}%</div>
      </div>
      <div class="pop-age-group">
        <div class="pop-age-icon">👤</div>
        <div class="pop-age-info">
          <div class="pop-age-value">${formatPop(pop1564)}</div>
          <div class="pop-age-label">15–64 ani</div>
        </div>
        <div class="pop-age-pct">${popTotal > 0 ? ((pop1564 / popTotal) * 100).toFixed(0) : 0}%</div>
      </div>
      <div class="pop-age-group pop-age-vulnerable">
        <div class="pop-age-icon">👴</div>
        <div class="pop-age-info">
          <div class="pop-age-value">${formatPop(popGe65)}</div>
          <div class="pop-age-label">≥ 65 ani</div>
        </div>
        <div class="pop-age-pct">${popTotal > 0 ? ((popGe65 / popTotal) * 100).toFixed(0) : 0}%</div>
      </div>
    </div>

    <div class="pop-panel-breakdown" style="margin-top: 10px;">
      <div class="pop-age-group">
        <div class="pop-age-icon">👩</div>
        <div class="pop-age-info">
          <div class="pop-age-value">${formatPop(popFemale)}</div>
          <div class="pop-age-label">Femei</div>
        </div>
        <div class="pop-age-pct">${popTotal > 0 ? ((popFemale / popTotal) * 100).toFixed(0) : 0}%</div>
      </div>
      <div class="pop-age-group">
        <div class="pop-age-icon">👨</div>
        <div class="pop-age-info">
          <div class="pop-age-value">${formatPop(popMale)}</div>
          <div class="pop-age-label">Bărbați</div>
        </div>
        <div class="pop-age-pct">${popTotal > 0 ? ((popMale / popTotal) * 100).toFixed(0) : 0}%</div>
      </div>
      <div class="pop-age-group" style="background: rgba(33, 150, 243, 0.1); border: 1px solid rgba(33, 150, 243, 0.2);">
        <div class="pop-age-icon">💼</div>
        <div class="pop-age-info">
          <div class="pop-age-value">${formatPop(popEmployed)}</div>
          <div class="pop-age-label" style="color: #64b5f6;">Angajați</div>
        </div>
        <div class="pop-age-pct" style="color: #90caf9;">${popTotal > 0 ? ((popEmployed / popTotal) * 100).toFixed(0) : 0}%</div>
      </div>
    </div>

    <div class="pop-panel-area">
      <span class="pop-area-value">${cellCount.toLocaleString()}</span> celule · 
      <span class="pop-area-value">~${formatArea(areaSqKm)}</span>
    </div>

    ${renderAverages(avgs)}
  `;

  // Buton clear
  const clearBtn = panelEl.querySelector('.pop-panel-clear');
  if (clearBtn) {
    clearBtn.addEventListener('click', () => clearAllSelections());
  }
}

function renderAverages(avgs) {
  const entries = Object.entries(avgs).filter(([k]) => k.startsWith('avg_'));
  if (entries.length === 0) return '';

  const items = entries.map(([k, v]) => {
    const variable = k.replace('avg_', '');
    const label = LABELS[variable] || variable;
    const unit = UNITS[variable] || '';
    const val = v != null ? Number(v).toFixed(1) : '—';
    return `<div class="pop-avg-item">
      <span class="pop-avg-label">${label}</span>
      <span class="pop-avg-value">${val} <small>${unit}</small></span>
    </div>`;
  }).join('');

  return `<div class="pop-panel-averages">
    <div class="pop-avg-title">Medii în selecție</div>
    ${items}
  </div>`;
}

function formatPop(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000).toLocaleString()}K`;
  return n.toLocaleString();
}

function formatArea(sqkm) {
  if (sqkm >= 1_000) return `${(sqkm / 1_000).toFixed(0)}K km²`;
  return `${sqkm.toLocaleString()} km²`;
}
